import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@attmos/flowstack-ui";

import { useBudget, type BudgetSummary, type CostLineItem, type TileSpend } from "@/api/budget";
import { cn } from "@/lib/utils";

export function BudgetPage() {
  const { data, isLoading, isError } = useBudget();

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
        Loading budget…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card elevation="raised">
        <CardHeader>
          <CardTitle>Could not load budget</CardTitle>
          <CardDescription>The FlowStack server returned an error.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">Budget</h1>
          {/* Estimate badge is driven by the server's pricing_basis
              (U0.3, C0.5): rendered only for "placeholder-estimate";
              absent field (older server) ⇒ no badge. */}
          {data.pricing_basis === "placeholder-estimate" && (
            <span className="label-micro rounded-sm bg-muted px-1.5 py-0.5 text-muted-foreground">
              estimate
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Tenant-scoped cost ledger per anchor §3.7. Period{" "}
          <span className="font-mono">{data.period_start}</span> to{" "}
          <span className="font-mono">{data.period_end}</span>.
        </p>
      </div>

      <HeadlineNumbers data={data} />

      <section aria-labelledby="line-items-heading">
        <h2 id="line-items-heading" className="mb-2 text-lg font-semibold">
          Current month by category
        </h2>
        <LineItemsTable items={data.line_items} />
      </section>

      <section aria-labelledby="top-tiles-heading">
        <h2 id="top-tiles-heading" className="mb-2 text-lg font-semibold">
          Top spending tiles
        </h2>
        {data.top_tiles.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tile runs yet this month.</p>
        ) : (
          <TopTilesTable tiles={data.top_tiles} />
        )}
      </section>
    </div>
  );
}

function HeadlineNumbers({ data }: { data: BudgetSummary }) {
  const pctOfBudget =
    data.monthly_budget_usd && data.monthly_budget_usd > 0
      ? Math.round((data.current_month_usd / data.monthly_budget_usd) * 100)
      : null;
  const overBudget = pctOfBudget !== null && pctOfBudget > 100;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <StatCard label="Current month">
        <p className="text-2xl font-semibold tabular-nums">{formatUsd(data.current_month_usd)}</p>
        {/* Show the X% caption only when we have a usable cap to
            divide by. Gating on pctOfBudget !== null (rather than the
            raw monthly_budget_usd !== null) closes the
            monthly_budget_usd === 0 edge case — rendering
            "% of $0.00 cap" would be confusing copy. PR-23 r1
            addressing Rupert's review on #199. */}
        {pctOfBudget !== null ? (
          <p
            className={cn(
              "mt-1 text-xs",
              overBudget ? "font-medium text-destructive" : "text-muted-foreground"
            )}
          >
            {pctOfBudget}% of {formatUsd(data.monthly_budget_usd!)} cap
          </p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">No monthly cap set</p>
        )}
      </StatCard>
      <StatCard label="Total to date">
        <p className="text-2xl font-semibold tabular-nums">{formatUsd(data.total_usd)}</p>
      </StatCard>
      <StatCard label="Monthly budget">
        <p className="text-2xl font-semibold tabular-nums">
          {data.monthly_budget_usd !== null ? formatUsd(data.monthly_budget_usd) : "—"}
        </p>
        {/* P0 doesn't enforce budget caps — the cost ledger only
            observes. Earlier copy said "Cap enforced at submission"
            which would mislead pilot users into thinking their
            submissions get gated. PR-23 r1 — Rupert review on #199. */}
        <p className="mt-1 text-xs text-muted-foreground">
          {data.monthly_budget_usd !== null ? "Observed, not enforced in P0" : "Unbounded"}
        </p>
      </StatCard>
    </div>
  );
}

function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // Was a hand-rolled `rounded-md border bg-card p-card` — the second card
    // recipe, in the third spelling. `rounded-md` -> `rounded-lg` is the only
    // pixel it moves. flowstack#1055.
    <Card className="p-card">
      <p className="label-micro text-muted-foreground">{label}</p>
      <div className="mt-1">{children}</div>
    </Card>
  );
}

function LineItemsTable({ items }: { items: CostLineItem[] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader surface="tinted">
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead align="right">Current month</TableHead>
            <TableHead align="right">Total to date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.category}>
              <TableCell className="font-mono text-xs">{it.category}</TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(it.current_month_usd)}
              </TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(it.total_usd)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function TopTilesTable({ tiles }: { tiles: TileSpend[] }) {
  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader surface="tinted">
          <TableRow>
            <TableHead>Tile</TableHead>
            <TableHead align="right">Runs</TableHead>
            <TableHead align="right">Current month</TableHead>
            <TableHead align="right">Total to date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tiles.map((t) => (
            <TableRow key={t.tile_name}>
              <TableCell className="font-mono text-xs">{t.tile_name}</TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {t.run_count}
              </TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(t.current_month_usd)}
              </TableCell>
              <TableCell align="right" className="text-xs tabular-nums">
                {formatUsd(t.total_usd)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
